import { FC, useState, useMemo } from "react";
import { useOutletContext, useParams } from "react-router";

import {
  CampaignLinkedCategories,
  CategoryTrees,
  CategoryFilterType,
  CategoryType,
  CategoryTreeObject,
  LinkedCategory,
} from "@vviinn/user-managment-client-ts";

import { useAppSelector } from "../../../../../store";
import { useRequestHandlers } from "../../../../../app/hooks/useRequestHandlers";
import { SourceOrTarget } from "../../../../types";
import { categoryFilterTypeSettings } from "../../../../constants";
import { AssignedCategories } from "../../../../category";
import { CategoryTree } from "./CategoryTree";

import { SlideOverWrapper } from "../../../../../ui/layout/organisms/SlideOver/SlideOverWrapper/SlideOverWrapper";
import { NamedSelect } from "../../../../../ui/layout/molecules/NamedSelect";
import { ROW_SUBTITLE_STYLES } from "../../../../../ui/layout/detailedPages/styles";
import { handleCampaign } from "../../../../../store/features/campaigns/handleCampaign";
import { CompanyAndSiteIds } from "../../../../../routes/types";

type SlideOverCampaignCategoriesProps = {
  assignedCategories: CampaignLinkedCategories;
  slideOpen: boolean;
  onSlideClose: () => void;
  tree?: CategoryTrees;
  isVpcCampaign: boolean;
  categoriesAmount: number;
};

enum MapCategoryFilterTypeAndCategoryType {
  "google_product_category" = "google_product_category",
  "product_type_category" = "product_type",
}

enum MapCategoryFilterTypeAndTreeProperties {
  "google_product_category" = "googleFeedCategory",
  "product_type_category" = "productTypeCategory",
}

const ASSIGNED_ITEMS_TITLE_STYLES = `text-primary text-xs ${ROW_SUBTITLE_STYLES}`;

const tabs: SourceOrTarget[] = ["source", "target"];

const initialTabIndex = 1;
const initialCategoryFilterType = categoryFilterTypeSettings[0].value;

export const SlideOverCampaignCategories: FC<
  React.PropsWithChildren<SlideOverCampaignCategoriesProps>
> = ({ tree, assignedCategories, slideOpen, onSlideClose, isVpcCampaign }) => {
  const { companyId, siteId } = useOutletContext<CompanyAndSiteIds>();

  const { campaignId } = useParams();
  const campaign = useAppSelector((state) =>
    state.campaigns.campaigns.find((c) => c.id === campaignId),
  );

  const handlerParams = useRequestHandlers();
  const { handleCategoryAction } = handleCampaign(handlerParams, {
    companyId,
    siteId,
  });

  const [activeTabIndex, setActiveTabIndex] = useState(initialTabIndex);

  const activeTab = useMemo(() => tabs[activeTabIndex], [tabs, activeTabIndex]);

  const [categoryFilterType, setCategoryFilterType] =
    useState<CategoryFilterType>(initialCategoryFilterType);

  const assignedGoogleFeedCategories =
    assignedCategories[activeTab]?.googleFeedCategories;
  const assignedProductTypeCategories =
    assignedCategories[activeTab]?.productTypeCategories;

  const assignedTypeCategories =
    categoryFilterType === "google_product_category"
      ? assignedCategories[activeTab]?.googleFeedCategories
      : assignedCategories[activeTab]?.productTypeCategories;

  const requestCategoryType =
    MapCategoryFilterTypeAndCategoryType[categoryFilterType];

  const categoriesFeedTabTree =
    tree?.[MapCategoryFilterTypeAndTreeProperties[categoryFilterType]];

  const categoriesProductTypeTabTree = tree?.productTypeCategory;
  const categoriesGoogleFeedTabTree = tree?.googleFeedCategory;

  const onAssign = async (treeNode: CategoryTreeObject) => {
    if (!campaign) return;
    await handleCategoryAction(
      campaign,
      "assignCategory",
      requestCategoryType,
      treeNode,
      activeTab,
    );
  };

  const onUnassign =
    (categoryType: CategoryType) => async (treeNode: CategoryTreeObject) => {
      if (!campaign) return;
      await handleCategoryAction(
        campaign,
        "unassignCategory",
        categoryType,
        treeNode,
        activeTab,
      );
    };

  const AssignedCategoriesNode = (
    type: "google" | "product",
    assignedCategories: LinkedCategory[],
    categoryTree: CategoryTreeObject,
  ) => (
    <section className="max-w-full truncate grid gap-2">
      <h4
        className={ASSIGNED_ITEMS_TITLE_STYLES}
      >{`${type === "product" ? "Product Type" : "Google Product"} Categories (${assignedCategories.length})`}</h4>
      <AssignedCategories
        assignedCategories={assignedCategories}
        list={categoryTree}
        onUnassign={onUnassign(
          type === "google" ? "google_product_category" : "product_type",
        )}
      />
    </section>
  );

  return (
    <SlideOverWrapper
      open={slideOpen}
      onClose={onSlideClose}
      initConfig={() => {
        setActiveTabIndex(initialTabIndex);
        setCategoryFilterType(initialCategoryFilterType);
      }}
      title="Assigned Products"
      tabProps={
        isVpcCampaign
          ? { tabs, setActiveTabIndex, tabGroupReference: "category" }
          : undefined
      }
    >
      {!!assignedProductTypeCategories?.length &&
        categoriesProductTypeTabTree &&
        AssignedCategoriesNode(
          "product",
          assignedProductTypeCategories,
          categoriesProductTypeTabTree,
        )}
      {!!assignedGoogleFeedCategories?.length &&
        categoriesGoogleFeedTabTree &&
        AssignedCategoriesNode(
          "google",
          assignedGoogleFeedCategories,
          categoriesGoogleFeedTabTree,
        )}

      {/* Main Content Starts */}
      <section>
        <h2 className="text-primary text-md font-medium max-sm:mb-1">
          Choose Product Categories
        </h2>

        <NamedSelect
          title="product categories"
          options={categoryFilterTypeSettings}
          onChange={(target) =>
            setCategoryFilterType(target.value as CategoryFilterType)
          }
          styleVariant="detailPage"
          value={categoryFilterType}
        />

        {categoriesFeedTabTree && (
          <CategoryTree
            tree={categoriesFeedTabTree}
            assignedCategories={assignedTypeCategories ?? []}
            onAssign={onAssign}
            onUnassign={onUnassign(requestCategoryType)}
            className="mt-4"
          />
        )}
      </section>
    </SlideOverWrapper>
  );
};
